import { Menu, MenuItem } from "@blueprintjs/core";
import { ContextMenu2 } from "@blueprintjs/popover2";
import classNames from "classnames";
import { selectAll } from "d3";
import React, { ReactNode } from "react";

import { usePlayContext } from "../../../../pages/Play/play-context";
import { Sport } from "../../../../redux/slices";
import { ActionLineType, FrameStencil } from "../../../../types";
import {
  BasketballSelectActionMenu,
  HockeySelectActionMenu,
  SoccerSelectActionMenu,
} from "../../../select-action-menu";
import styles from "./playing-surface-context-menu.module.scss";

export interface PlayingSurfaceContextMenuProps {
  isEditable?: boolean;
  stencils: FrameStencil[];
  children: ReactNode;
}

export const PlayingSurfaceContextMenu = ({
  isEditable = false,
  stencils,
  children,
}: PlayingSurfaceContextMenuProps) => {
  const { frameStencilsApi, selectItemApi, playStateApi } = usePlayContext();
  const { sport } = playStateApi.playState;
  const { updateActionType, removeStencil, clearStencils } = frameStencilsApi;

  const { selectItem } = selectItemApi;

  /** ======== Change Action Type ======== */
  const changeActionType = (id: string, layerType: ActionLineType) => {
    updateActionType({ id, layerType });
    selectItem(id);
  };

  return (
    <ContextMenu2
      className={classNames(styles.courtContextMenu, "bp4-dark")}
      disabled={!isEditable}
      content={(props) => {
        // eslint-disable-next-line react/prop-types
        const { mouseEvent } = props;
        // eslint-disable-next-line react/prop-types
        const target = mouseEvent?.target;
        const d3StencilWrapper = selectAll<SVGGElement, unknown>(".stencil-wrapper");
        const stencilWrapperNodes = d3StencilWrapper.nodes();
        const isWithinStencil = stencilWrapperNodes.some((node) => node.contains(target as SVGElement));

        if (isWithinStencil) {
          const parentStencilWrapper = stencilWrapperNodes.find((node) => node.contains(target as SVGElement));
          if (parentStencilWrapper) {
            const stencilId = parentStencilWrapper.id.replace("editable-play-surface", "");
            const enableActionType = stencils.some((s) => s.id === stencilId && s.stencilType === "ACTION");

            return (
              <Menu>
                {enableActionType && (
                  <MenuItem text="Change Action Type" submenuProps={{ className: styles.changeActionTypeMenu }}>
                    {((): JSX.Element => {
                      switch (sport) {
                        case Sport.BASKETBALL:
                          return (
                            <BasketballSelectActionMenu
                              handleClickAction={({ layerType }) => changeActionType(stencilId, layerType)}
                            />
                          );

                        case Sport.HOCKEY:
                          return (
                            <HockeySelectActionMenu
                              handleClickAction={({ layerType }) => changeActionType(stencilId, layerType)}
                            />
                          );

                        case Sport.SOCCER:
                          return (
                            <SoccerSelectActionMenu
                              handleClickAction={({ layerType }) => changeActionType(stencilId, layerType)}
                            />
                          );
                      }
                    })()}
                  </MenuItem>
                )}
                <MenuItem text="Remove" onClick={() => removeStencil({ id: stencilId })} />
              </Menu>
            );
          } else return undefined;
        } else
          return (
            <Menu>
              <MenuItem text="Clear Frame" onClick={clearStencils} />
            </Menu>
          );
      }}
    >
      {children}
    </ContextMenu2>
  );
};
